/**
 * "My Downloads" — walks the customer's order history and reveals the findings (NWF-192).
 *
 * ## Why a walk at all
 *
 * Liquid hands out at most 20 orders per render — a documented limit of `customer.orders`,
 * not a theme setting — and it has no server side filter, so there is no way to ask the
 * storefront for "every order with a download". The section therefore renders the findings of
 * its own group, and this walks the remaining groups through the Section Rendering API and
 * collects what they found. Each response carries this section only, and a group without
 * downloads renders almost nothing, so the requests stay small.
 *
 * ## The promise the button makes
 *
 * "Show more" is bound to entries that are FOUND but hidden, never to "there might be more
 * orders". It can therefore never come up empty. Only when the walk hits its cap does the
 * button change its label to a search — that click may find nothing, and the wording says so.
 *
 * ## The cap
 *
 * 25 groups, so 500 orders, per run. Without it a customer with a thousand orders would fire
 * fifty requests on every visit. Nothing is hidden by the cap; it costs a click.
 */
class NwfMyDownloads extends HTMLElement {
    connectedCallback() {
        this.list = this.querySelector('[data-nwf-downloads-list]');
        if (!this.list) return;

        this.meta = this.querySelector('[data-nwf-downloads-meta]');
        this.counter = this.querySelector('[data-nwf-downloads-count]');
        this.empty = this.querySelector('[data-nwf-downloads-empty]');
        this.status = this.querySelector('[data-nwf-downloads-status]');
        this.button = this.querySelector('[data-nwf-downloads-more]');
        this.buttonLabel = this.querySelector('[data-nwf-downloads-more-label]');

        this.pageSize = parseInt(this.dataset.pageSize, 10) || 20;
        this.maxPages = parseInt(this.dataset.maxPages, 10) || 25;
        // Not the number of rendered entries: Liquid can hand over more than one batch, and
        // taking its count as the visible count would show everything and hide the button.
        // render() raises this to a full batch on the first pass.
        this.shown = 0;

        this.button?.addEventListener('click', () => this.onButtonClick());

        // Set before the first paint: without it a customer whose downloads sit in an older
        // group sees "no downloads yet" for a moment before the walk fills the list.
        this.pending = Boolean(this.list.dataset.nextPage);

        this.render();
        if (this.pending) this.walk();
    }

    onButtonClick() {
        const found = this.list.children.length;

        if (found > this.shown) {
            this.shown += this.pageSize;
            this.render();
            return;
        }

        // Nothing left to reveal, so the cap stopped the walk — carry on searching.
        this.walk();
    }

    /** Fetches group after group until the history ends, the cap is reached, or one fails. */
    async walk() {
        if (this.walking) return;
        this.walking = true;

        this.toggle(this.button, false);
        this.toggle(this.status, true);

        let pages = 0;

        while (this.list.dataset.nextPage && pages < this.maxPages) {
            let group;

            try {
                group = await this.fetchGroup(this.list.dataset.nextPage);
            } catch (error) {
                // Keep what was found. A short list beats a page that looks broken.
                console.error('nwf-my-downloads: could not load further orders', error);
                break;
            }

            pages += 1;

            const source = group.querySelector('[data-nwf-downloads-list]');
            if (!source) break;

            Array.from(source.children).forEach((item) => this.list.appendChild(item));

            if (source.dataset.nextPage) {
                this.list.dataset.nextPage = source.dataset.nextPage;
            } else {
                delete this.list.dataset.nextPage;
            }

            // Repaint per group, so a long walk fills the list as it goes.
            this.render();
        }

        this.walking = false;
        this.pending = false;
        this.toggle(this.status, false);
        this.render();
    }

    async fetchGroup(page) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('page', page);

        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    /** Count, visibility and button follow the list; nothing here keeps its own state. */
    render() {
        const items = Array.from(this.list.children);
        const found = items.length;

        if (this.shown < this.pageSize) this.shown = Math.min(this.pageSize, found);

        items.forEach((item, index) => {
            item.hidden = index >= this.shown;
        });

        if (this.counter) {
            const template = found === 1 ? this.dataset.countOne : this.dataset.countOther;
            if (template) this.counter.textContent = template.replace('%%', found);
        }

        this.toggle(this.meta, found > 0);
        this.toggle(this.empty, found === 0 && !this.walking && !this.pending);

        this.renderButton(found);
    }

    renderButton(found) {
        if (!this.button) return;

        if (this.walking) {
            this.toggle(this.button, false);
            return;
        }

        if (found > this.shown) {
            this.setButton(this.button.dataset.labelMore, true);
            return;
        }

        // Entries are all visible. Only an interrupted walk still has something to offer.
        this.setButton(this.button.dataset.labelSearch, Boolean(this.list.dataset.nextPage));
    }

    setButton(label, visible) {
        if (this.buttonLabel && label) this.buttonLabel.textContent = label;
        this.toggle(this.button, visible);
    }

    toggle(element, visible) {
        if (!element) return;
        element.hidden = !visible;
    }
}

customElements.define('nwf-my-downloads', NwfMyDownloads);
